import { Fragment, useMemo, useState } from "react";
import { ArrowLeft, Wrench, Edit2, Trash2, SlidersHorizontal } from "lucide-react";
import Button from "../ui/Button";
import Card from "../ui/Card";
import MetricCard from "../ui/MetricCard";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import Pill from "../ui/Pill";
import ConfirmDialog from "../ui/ConfirmDialog";
import ToolForm from "./ToolForm";
import { formatUSD } from "../../lib/formatters";
import { formatDate, monthKey, today } from "../../lib/dateUtils";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Compute the signed amount for a transaction (refunds count as negative so
// they reduce the tool's monthly cost). Mirrors ActualsMatrix exactly so the
// number inside this view always matches the number in the matrix outside.
function signedAmount(t) {
  const sign = (t.direction || "out") === "out" ? 1 : -1;
  return sign * (Number(t.amount) || 0);
}

// Mirror of calculations.costFromAllocations' per-row amount math (line 682)
// so we can render a single allocation's $ contribution on the profile without
// re-running the whole P&L aggregator.
function allocAmountFromBase(alloc, base) {
  const v = Number(alloc.value) || 0;
  return alloc.mode === "amount" ? v : (Number(base) || 0) * (v / 100);
}

function allocScope(alloc) {
  return alloc.scope || (alloc.offerId ? "offer" : null);
}

// Human label for an allocation's target (used in the row's middle column).
function allocTargetLabel(alloc, offers) {
  const scope = allocScope(alloc);
  if (scope === "offer") {
    const o = offers.find((x) => x.id === alloc.offerId);
    return o ? o.name : "(deleted offer)";
  }
  if (scope === "department") return alloc.scopeId || "(no department)";
  if (scope === "agency") return "All offers (agency overhead)";
  return "Unassigned";
}

// One-line explanation of which P&L view the cost actually lands on, so Ahmed
// knows where to look (Overview vs Department drill-down vs Offer drill-down).
function allocPnlHint(alloc, offers) {
  const scope = allocScope(alloc);
  const ct = (alloc.costType || "cogs").toUpperCase();
  if (scope === "offer") {
    const o = offers.find((x) => x.id === alloc.offerId);
    const dept = o?.department ? ` (rolls into ${o.department} dept + Agency P&L)` : " (rolls into Agency P&L)";
    return `Hits ${ct} on offer "${o?.name || "?"}"${dept}`;
  }
  if (scope === "department") {
    return `Hits ${ct} on ${alloc.scopeId || "?"} department P&L (and Agency P&L)`;
  }
  if (scope === "agency") {
    return `Hits ${ct} on Agency P&L only — visible on Overview. Does NOT drag any department's margins.`;
  }
  return "No scope set — cost is unattributed and won't show in any P&L.";
}

// Base monthly cost used by the actual P&L aggregator. Mirrors
// resolveToolMonthBase in calculations.js (sum of matched bank-feed
// expenses for this tool in that month, excluding flagged rows).
function toolMonthBase(toolId, mKey, oneOffs) {
  let amt = 0;
  for (const p of oneOffs || []) {
    if (p.matchedSourceType !== "tool" || p.matchedSourceId !== toolId) continue;
    if (!p.date || monthKey(p.date) !== mKey) continue;
    if (p.status === "flagged") continue;
    const sign = (p.direction || "out") === "out" ? 1 : -1;
    amt += sign * (Number(p.amount) || 0);
  }
  return amt > 0 ? amt : 0;
}

export default function ToolProfile({ tool, state, dashboard, onBack }) {
  const transactions = state.oneOffPayments || [];
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // Every row matched to this tool. No filters — what shows here is what
  // counts toward the matrix total. Sorted ascending by date so monthly
  // subtotals read naturally top-to-bottom.
  const matched = useMemo(
    () => transactions
      .filter((t) => t.matchedSourceType === "tool" && t.matchedSourceId === tool.id)
      .sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [transactions, tool.id]
  );

  const year = today().getFullYear();
  const currentMonthKey = `${year}-${String(today().getMonth() + 1).padStart(2, "0")}`;

  // Planned cost from the Tools tab (units × price per month). Kept around
  // for the variance label; the headline card now uses matched bank-feed
  // actuals — same source of truth the P&L aggregator uses.
  const monthlyPlanned = MONTH_LABELS.map((_, i) => {
    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
    const u = Number(tool.unitsByMonth?.[mKey] || 0);
    const p = Number(tool.priceByMonth?.[mKey] || 0);
    return u * p;
  });
  const plannedYtd = monthlyPlanned
    .filter((_, i) => `${year}-${String(i + 1).padStart(2, "0")}` <= currentMonthKey)
    .reduce((s, v) => s + v, 0);
  const currentMonthPlanned = monthlyPlanned[today().getMonth()];

  // Actuals for the active year using signed amounts (refunds subtract).
  const inYear = matched.filter((t) => t.date && t.date.startsWith(`${year}-`));
  const actualYtd = inYear.reduce((s, t) => s + signedAmount(t), 0);
  const currentMonthActual = inYear
    .filter((t) => monthKey(t.date) === currentMonthKey)
    .reduce((s, t) => s + signedAmount(t), 0);

  const variance = actualYtd - plannedYtd;

  // ── Allocations: $ contribution per row, this month + YTD ───────────────
  // These numbers mirror what the P&L aggregator does so the profile and the
  // Agency/Department/Offer P&L views always agree on the same dollars.
  const allocations = Array.isArray(tool.offerAllocations) ? tool.offerAllocations : [];
  const offers = state.offers || [];

  const allocRows = useMemo(() => {
    const currentBase = toolMonthBase(tool.id, currentMonthKey, transactions);
    return allocations.map((a) => {
      let ytdAmt = 0;
      for (let i = 0; i < 12; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        if (mKey > currentMonthKey) break;
        const base = toolMonthBase(tool.id, mKey, transactions);
        if (base <= 0) continue;
        ytdAmt += allocAmountFromBase(a, base);
      }
      return {
        alloc: a,
        thisMonth: currentBase > 0 ? allocAmountFromBase(a, currentBase) : 0,
        ytd: ytdAmt,
      };
    });
  }, [allocations, tool.id, transactions, currentMonthKey, year]);

  const allocTotalThisMonth = allocRows.reduce((s, r) => s + r.thisMonth, 0);
  const allocTotalYtd = allocRows.reduce((s, r) => s + r.ytd, 0);
  const currentMonthCost = toolMonthBase(tool.id, currentMonthKey, transactions);
  const unattributedThisMonth = Math.max(0, currentMonthCost - allocTotalThisMonth);

  // Group year-rows by month for the breakdown table.
  const monthGroups = useMemo(() => {
    const groups = new Map(); // monthKey -> { rows, subtotal }
    for (const t of inYear) {
      const mKey = monthKey(t.date);
      if (!mKey) continue;
      const g = groups.get(mKey) || { rows: [], subtotal: 0 };
      g.rows.push(t);
      g.subtotal += signedAmount(t);
      groups.set(mKey, g);
    }
    const out = [];
    for (const [mKey, g] of [...groups.entries()].sort()) {
      const monthIdx = Number(mKey.slice(5, 7)) - 1;
      out.push({ mKey, label: MONTH_LABELS[monthIdx], rows: g.rows, subtotal: g.subtotal });
    }
    return out;
  }, [inYear]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Tools
        </Button>
        {dashboard && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <Edit2 size={13} /> Edit
            </Button>
            <Button
              variant="danger"
              onClick={() => setConfirm({
                title: `Delete ${tool.name}?`,
                message: `Permanently delete this tool. Allocations, planned costs and any expenses matched to this tool will lose the link (the underlying expense rows are NOT deleted, just unlinked). This cannot be undone.`,
                confirmLabel: "Delete",
                onConfirm: () => {
                  dashboard.deleteTool(tool.id);
                  setConfirm(null);
                  onBack?.();
                },
              })}
            >
              <Trash2 size={13} /> Delete
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Wrench size={16} className="text-brand-slate" />
            <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">{tool.name}</h2>
            {tool.status === "inactive" && <Pill status="paused">Inactive</Pill>}
            {tool.type === "oneoff" && <Pill status="oneoff">One-off</Pill>}
            {tool.type === "variable" && <Pill status="neutral">Variable</Pill>}
            {(!tool.type || tool.type === "recurring") && <Pill status="recurring">Recurring</Pill>}
          </div>
          {tool.category && <div className="mt-1 text-xs text-brand-slate">Category: {tool.category}</div>}
          {tool.notes && <div className="mt-1 text-xs text-brand-slate max-w-xl">{tool.notes}</div>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Current month cost"
          value={formatUSD(currentMonthCost)}
          accent="blue"
          sublabel="from matched bank feed"
        />
        <MetricCard
          label="Current month actual"
          value={formatUSD(currentMonthActual)}
          accent={currentMonthActual > currentMonthPlanned ? "red" : "green"}
          sublabel="net of refunds"
        />
        <MetricCard
          label={`${year} planned YTD`}
          value={formatUSD(plannedYtd)}
          accent="amber"
          sublabel="from Tools tab"
        />
        <MetricCard
          label={`${year} actual YTD`}
          value={formatUSD(actualYtd)}
          accent={variance > 0 ? "red" : variance < 0 ? "green" : "default"}
          sublabel={
            variance > 0
              ? `+${formatUSD(variance)} over plan`
              : variance < 0
              ? `${formatUSD(Math.abs(variance))} under plan`
              : "on plan"
          }
        />
      </div>

      <Card
        title={<span className="inline-flex items-center gap-2"><SlidersHorizontal size={14} /> Allocations · where the cost lands in P&amp;L</span>}
        action={dashboard && (
          <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
            <Edit2 size={12} /> Edit allocations
          </Button>
        )}
      >
        {allocations.length === 0 ? (
          <EmptyState
            icon={SlidersHorizontal}
            title="No allocations set"
            message="This tool's cost isn't being attributed to any P&L. Click Edit allocations to route it to an Offer, Department, or Agency overhead."
          />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Scope</TH>
                  <TH>Target</TH>
                  <TH>Rule</TH>
                  <TH>Type</TH>
                  <TH className="text-right">This month</TH>
                  <TH className="text-right">YTD</TH>
                </TR>
              </THead>
              <tbody>
                {allocRows.map(({ alloc, thisMonth, ytd }, idx) => {
                  const scope = allocScope(alloc);
                  const ct = (alloc.costType || "cogs");
                  const scopeStyle =
                    scope === "agency"
                      ? "bg-purple-50 text-purple-700 border-purple-200"
                      : scope === "department"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-gray-50 text-gray-700 border-gray-200";
                  const ctStyle =
                    ct === "cogs"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-amber-50 text-amber-700 border-amber-200";
                  return (
                    <Fragment key={idx}>
                      <TR>
                        <TD>
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${scopeStyle}`}>
                            {scope || "—"}
                          </span>
                        </TD>
                        <TD className="font-medium">{allocTargetLabel(alloc, offers)}</TD>
                        <TD className="text-xs text-brand-slate">
                          {alloc.mode === "amount"
                            ? `$${(Number(alloc.value) || 0).toLocaleString()} fixed`
                            : `${Number(alloc.value) || 0}% of cost`}
                        </TD>
                        <TD>
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${ctStyle}`}>
                            {ct}
                          </span>
                        </TD>
                        <TD className="text-right font-medium">{formatUSD(thisMonth)}</TD>
                        <TD className="text-right font-medium">{formatUSD(ytd)}</TD>
                      </TR>
                      <TR className="bg-gray-50/60 dark:bg-[var(--bg-soft)]/40 border-t-0">
                        <TD colSpan={6} className="text-[11px] text-brand-slate italic pt-0">
                          → {allocPnlHint(alloc, offers)}
                        </TD>
                      </TR>
                    </Fragment>
                  );
                })}
                <TR className="bg-gray-100 dark:bg-[var(--bg-soft)]/80 border-t-2 border-gray-200 dark:border-[var(--border-soft)]">
                  <TD colSpan={4} className="font-semibold uppercase tracking-wide text-[10px] text-brand-slate">
                    Allocated total
                  </TD>
                  <TD className="text-right font-bold">{formatUSD(allocTotalThisMonth)}</TD>
                  <TD className="text-right font-bold">{formatUSD(allocTotalYtd)}</TD>
                </TR>
                {unattributedThisMonth > 0.01 && (
                  <TR>
                    <TD colSpan={4} className="text-[11px] text-red-600 font-medium">
                      Unattributed this month (not hitting any P&amp;L)
                    </TD>
                    <TD className="text-right font-medium text-red-600">{formatUSD(unattributedThisMonth)}</TD>
                    <TD />
                  </TR>
                )}
              </tbody>
            </Table>
            <p className="mt-3 text-[11px] text-brand-slate leading-relaxed">
              Percent allocations are applied to the matched bank-feed cost for the month. Fixed-amount allocations apply regardless of matched cost.
              Cost lands on Agency, Department, or Offer P&amp;L based on the scope of each row — Agency-scope rows only show on the <strong>Overview</strong> tab.
            </p>
          </>
        )}
      </Card>

      <Card title={`Assigned invoices · ${year} (${inYear.length})`}>
        {inYear.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="No transactions assigned yet"
            message="Match expense transactions to this tool in the Expenses tab to populate this view."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Name</TH>
                <TH>Type</TH>
                <TH className="text-right">Amount</TH>
              </TR>
            </THead>
            <tbody>
              {monthGroups.map((group) => (
                <Fragment key={group.mKey}>
                  {group.rows.map((t) => {
                    const isRefund = (t.direction || "out") !== "out";
                    return (
                      <TR key={t.id}>
                        <TD>{formatDate(t.date)}</TD>
                        <TD className="font-medium">
                          {t.name}
                          {t.qbType && (
                            <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border bg-gray-100 text-gray-600 border-gray-200">
                              {t.qbType}
                            </span>
                          )}
                        </TD>
                        <TD className="text-xs text-brand-slate">
                          {isRefund ? "Refund (–)" : "Charge"}
                        </TD>
                        <TD className={`text-right font-medium ${
                          isRefund
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-brand-ink dark:text-[var(--text-primary)]"
                        }`}>
                          {isRefund
                            ? `−${formatUSD(Math.abs(Number(t.amount) || 0))}`
                            : formatUSD(Number(t.amount) || 0)}
                          {t.currency && t.currency !== "USD" && (
                            <span className="ml-1 text-[9px] font-semibold text-brand-slate uppercase">{t.currency}</span>
                          )}
                        </TD>
                      </TR>
                    );
                  })}
                  <TR className="bg-gray-50 dark:bg-[var(--bg-soft)]/60 border-t border-gray-200 dark:border-[var(--border-soft)]">
                    <TD colSpan={3} className="font-semibold text-brand-slate uppercase tracking-wide text-[10px]">
                      {group.label} subtotal
                    </TD>
                    <TD className={`text-right font-bold ${group.subtotal < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                      {formatUSD(group.subtotal)}
                    </TD>
                  </TR>
                </Fragment>
              ))}
              <TR className="bg-brand-ink text-white border-t-2 border-gray-300 dark:border-[var(--border-soft)]">
                <TD colSpan={3} className="font-bold">
                  {year} total
                </TD>
                <TD className={`text-right font-bold ${actualYtd < 0 ? "text-emerald-300" : "text-white"}`}>
                  {formatUSD(actualYtd)}
                </TD>
              </TR>
            </tbody>
          </Table>
        )}
      </Card>

      {editOpen && dashboard && (
        <ToolForm
          open={true}
          onClose={() => setEditOpen(false)}
          initial={tool}
          onSubmit={(data) => {
            dashboard.updateTool(tool.id, data);
            setEditOpen(false);
          }}
          offers={state.offers || []}
          departments={state.settings?.departments || []}
        />
      )}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
    </div>
  );
}
